import { useEffect, useState, useRef } from 'react';
import { useOnlineStatus } from '@/context/OnlineStatusContext';

export default function OnlineStatusToast() {
  const { justReconnected, dismissReconnected } = useOnlineStatus();
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (justReconnected) {
      // Trigger entrance
      requestAnimationFrame(() => {
        setVisible(true);
        setExiting(false);
      });

      // Auto-dismiss after 4 seconds
      timerRef.current = setTimeout(() => {
        handleDismiss();
      }, 4000);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
  }, [justReconnected]);

  const handleDismiss = () => {
    setExiting(true);
    exitTimerRef.current = setTimeout(() => {
      setVisible(false);
      setExiting(false);
      dismissReconnected();
    }, 400);
  };

  if (!justReconnected && !visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed top-0 left-1/2 z-[9999] -translate-x-1/2 pt-4 px-4 w-full max-w-md pointer-events-none ${
        visible && !exiting ? 'animate-slide-down' : ''
      } ${exiting ? 'animate-slide-up-out' : ''}`}
    >
      <div className="pointer-events-auto mx-auto flex items-center gap-3 bg-foreground-950 text-background-50 rounded-xl px-5 py-3.5 shadow-lg max-w-sm">
        {/* Wifi reconnection icon */}
        <div className="w-9 h-9 rounded-full bg-accent-500/20 flex items-center justify-center flex-shrink-0">
          <i className="ri-wifi-fill text-accent-500 text-lg"></i>
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight text-background-50">
            You&apos;re back online!
          </p>
          <p className="text-xs text-background-50/60 leading-tight mt-0.5">
            Connection restored — keep browsing
          </p>
        </div>

        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 hover:bg-background-50/10 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-background-50/50 text-base"></i>
        </button>
      </div>
    </div>
  );
}